import {
  DISCOVERY_JOB_STATUSES,
  isDiscoveryJobStatus,
  type DiscoveryJobStatus,
} from '@discovr/types';

/** Above the creator-discovery service's 3-minute persona budget, below Go's 240 s gateway timeout. */
export const PERSONA_REQUEST_TIMEOUT_MS = 200_000;
export const SEARCH_START_TIMEOUT_MS = PERSONA_REQUEST_TIMEOUT_MS;

/**
 * A running search's status, stage counts and live creators: small answers, so they can come often. The service's
 * rate limit is per service token, so every admin watching a search shares it; one live search polls about 70
 * calls a minute.
 */
export const SEARCH_POLL_MS = 2_000;
export const LIVE_FEED_POLL_MS = 2_000;
export const PERSONA_TRACE_POLL_MS = 2_000;
/** A rate-limited poll slows down to at most this many times its usual interval. */
export const POLL_BACKOFF_MAX_FACTOR = 8;
export const RESULTS_POLL_MS = 15_000;
export const PICKS_POLL_MS = 30_000;
/** Go keeps a signed media link the same for an hour and honours it for up to two. */
export const MEDIA_LINKS_STALE_MS = 30 * 60_000;
export const MEDIA_LINKS_BATCH_MAX = 200;
export const MEDIA_NOT_SIGNED = 'Media link not signed';

export type DiscoveryJobPhase = 'live' | 'done' | 'stopped' | 'failed';

export const JOB_PHASE: Readonly<Record<DiscoveryJobStatus, DiscoveryJobPhase>> = {
  queued: 'live',
  running: 'live',
  done: 'done',
  partial: 'stopped',
  cancelled: 'stopped',
  stalled: 'stopped',
  error: 'failed',
};

export const jobPhase = (status: string): DiscoveryJobPhase | null =>
  isDiscoveryJobStatus(status) ? JOB_PHASE[status] : null;

export const LIVE_JOB_STATUSES: ReadonlySet<DiscoveryJobStatus> = new Set(
  DISCOVERY_JOB_STATUSES.filter((status) => JOB_PHASE[status] === 'live'),
);

const campaignDiscoveryPath = (campaignId: string) =>
  `/campaigns/${encodeURIComponent(campaignId)}/discovery`;
const searchPath = (campaignId: string, jobId: string) =>
  `${campaignDiscoveryPath(campaignId)}/searches/${encodeURIComponent(jobId)}`;

export const DISCOVERY_PATHS = {
  campaign: campaignDiscoveryPath,
  sync: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/sync`,
  generatePersonas: (campaignId: string) =>
    `${campaignDiscoveryPath(campaignId)}/personas/generate`,
  chatPersona: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/personas/chat`,
  writePersona: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/personas/write`,
  checkPersonaQuestion: (campaignId: string) =>
    `${campaignDiscoveryPath(campaignId)}/personas/check`,
  turnEvents: (campaignId: string, turnId: string) =>
    `${campaignDiscoveryPath(campaignId)}/personas/turns/${encodeURIComponent(turnId)}/events`,
  personaSearch: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/personas/search`,
  personaSearchPreview: (campaignId: string) =>
    `${campaignDiscoveryPath(campaignId)}/personas/search/preview`,
  personaStarters: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/personas/starters`,
  vettingCatalog: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/vetting/catalog`,
  chats: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/chats`,
  chat: (campaignId: string, chatId: string) =>
    `${campaignDiscoveryPath(campaignId)}/chats/${encodeURIComponent(chatId)}`,
  searches: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/searches`,
  estimate: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/searches/estimate`,
  search: searchPath,
  results: (campaignId: string, jobId: string) => `${searchPath(campaignId, jobId)}/results`,
  live: (campaignId: string, jobId: string) => `${searchPath(campaignId, jobId)}/live`,
  rejected: (campaignId: string, jobId: string) => `${searchPath(campaignId, jobId)}/rejected`,
  cancel: (campaignId: string, jobId: string) => `${searchPath(campaignId, jobId)}/cancel`,
  picks: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/picks`,
  mediaLinks: (campaignId: string) => `${campaignDiscoveryPath(campaignId)}/media-links`,
  media: (token: string) => `/discovery-media/${token}`,
} as const;
